import axios from "axios";
import {
  clearTokens,
  getAccessToken,
  getExpiresIn,
  getRefreshToken,
  setTokens,
} from "./authTokens";
import { fetchAndSetOrganization } from "./organizationService";
import {
  pausePostHogForImpersonation,
  resumePostHogAfterImpersonation,
} from "./posthogAnalytics";

const ADMIN_SESSION_KEY = "twilanswer_admin_session";
const IMPERSONATING_KEY = "twilanswer_impersonating";
const IMPERSONATION_LABEL_KEY = "twilanswer_impersonation_label";
const RETURN_PATH_KEY = "twilanswer_impersonation_return";

export function isImpersonating() {
  return localStorage.getItem(IMPERSONATING_KEY) === "1";
}

export function getImpersonationLabel() {
  return localStorage.getItem(IMPERSONATION_LABEL_KEY) || "support view";
}

export function getImpersonationReturnPath() {
  return localStorage.getItem(RETURN_PATH_KEY) || "/admin";
}

function stashAdminSession({ returnPath, label }) {
  const snapshot = {
    access_token: getAccessToken(),
    refresh_token: getRefreshToken(),
    expires_in: getExpiresIn(),
    user: localStorage.getItem("user"),
    organization: localStorage.getItem("organization"),
    returnPath: returnPath || "/admin",
    label: label || "",
  };
  localStorage.setItem(ADMIN_SESSION_KEY, JSON.stringify(snapshot));
}

/**
 * Restore stashed admin tokens/user/org. Clears impersonation flags.
 * Does not navigate.
 * @returns {{ ok: boolean, returnPath: string }}
 */
function restoreStashedAdminSession() {
  const raw = localStorage.getItem(ADMIN_SESSION_KEY);
  localStorage.removeItem(IMPERSONATING_KEY);
  localStorage.removeItem(IMPERSONATION_LABEL_KEY);

  if (!raw) {
    return { ok: false, returnPath: "/admin" };
  }

  try {
    const snapshot = JSON.parse(raw);
    const returnPath =
      snapshot.returnPath || localStorage.getItem(RETURN_PATH_KEY) || "/admin";
    clearTokens();
    setTokens({
      access_token: snapshot.access_token,
      refresh_token: snapshot.refresh_token,
      expires_in: snapshot.expires_in,
    });
    if (snapshot.user) localStorage.setItem("user", snapshot.user);
    else localStorage.removeItem("user");
    if (snapshot.organization) {
      localStorage.setItem("organization", snapshot.organization);
    } else {
      localStorage.removeItem("organization");
    }
    localStorage.removeItem(ADMIN_SESSION_KEY);
    localStorage.removeItem(RETURN_PATH_KEY);
    resumePostHogAfterImpersonation();
    return { ok: true, returnPath };
  } catch (err) {
    console.error("[Impersonation] Failed to restore admin session", err);
    return { ok: false, returnPath: "/admin" };
  }
}

function rollbackStartOrAbandon() {
  const restored = restoreStashedAdminSession();
  if (!restored.ok) {
    abandonImpersonationToLogin();
  }
}

/**
 * Start impersonation after admin/impersonate returns accessToken + user.
 * @param {{ accessToken: string, expiresIn?: string, user: object, orgId: string, label?: string, returnPath?: string }} opts
 */
export async function startImpersonation({
  accessToken,
  expiresIn,
  user: minimalUser,
  orgId,
  label,
  returnPath,
}) {
  if (!accessToken) throw new Error("Missing impersonation access token");
  if (!orgId) throw new Error("Missing orgId for impersonation");

  // Nested start would snapshot the impersonated session and lose the admin.
  if (isImpersonating()) {
    throw new Error(
      "Already in a support session. End it before impersonating another org."
    );
  }

  stashAdminSession({
    returnPath: returnPath || `/admin/orgs/${orgId}`,
    label,
  });

  localStorage.setItem(IMPERSONATING_KEY, "1");
  if (label) localStorage.setItem(IMPERSONATION_LABEL_KEY, label);
  localStorage.setItem(
    RETURN_PATH_KEY,
    returnPath || `/admin/orgs/${orgId}`
  );

  // Stop analytics before swapping identity into localStorage.
  pausePostHogForImpersonation();

  try {
    // Drop admin refresh token so we never refresh into the wrong identity.
    clearTokens();
    setTokens({
      access_token: accessToken,
      expires_in: expiresIn ?? "15m",
    });

    const userId =
      minimalUser?.userId?.S ||
      minimalUser?.userId ||
      minimalUser?.user_id?.S ||
      minimalUser?.user_id;

    let fullUser = minimalUser;
    if (userId) {
      try {
        const res = await axios.post(
          process.env.REACT_APP_BACKEND_URL + "get-user-by-id",
          { userId }
        );
        if (res?.data?.user) fullUser = res.data.user;
      } catch (err) {
        console.warn(
          "[Impersonation] get-user-by-id failed; using minimal user payload",
          err
        );
      }
    }

    localStorage.setItem("user", JSON.stringify(fullUser));
    await fetchAndSetOrganization(orgId);
  } catch (err) {
    rollbackStartOrAbandon();
    throw err;
  }

  window.location.href = "/dashboard/";
}

/**
 * Clear impersonation leftovers and send the admin to login.
 * Must re-enable PostHog: opt_out_capturing() persists in SDK storage, and
 * index.js only re-applies opt-out on boot — never opt-in.
 */
function abandonImpersonationToLogin() {
  localStorage.removeItem(RETURN_PATH_KEY);
  localStorage.removeItem(ADMIN_SESSION_KEY);
  clearTokens();
  localStorage.removeItem("user");
  localStorage.removeItem("organization");
  resumePostHogAfterImpersonation();
  window.location.href = "/login";
}

/**
 * Restore the stashed admin session and navigate back to admin.
 * @returns {boolean} whether a session was restored
 */
export function endImpersonation() {
  const restored = restoreStashedAdminSession();
  if (!restored.ok) {
    abandonImpersonationToLogin();
    return false;
  }

  window.location.href = restored.returnPath;
  return true;
}

/**
 * Called when an impersonation JWT expires (401, no refresh).
 * Restores admin session instead of dumping to login.
 */
export function handleImpersonationExpiry() {
  if (!isImpersonating()) return false;
  console.info(
    "[Auth] Impersonation session expired; restoring admin session."
  );
  return endImpersonation();
}
